"use client"

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import type { PanelComponent } from "./layout-model"

export interface LayoutApi {
  getClosedPanels: () => PanelComponent[]
  reopenPanel: (component: PanelComponent) => void
  resetLayout: () => void
}

interface LayoutContextValue {
  closedPanels: PanelComponent[]
  reopenPanel: (component: PanelComponent) => void
  resetLayout: () => void
  registerLayoutApi: (api: LayoutApi) => () => void
  /** Called by DockableLayout when panels are closed, opened, or resized */
  notifyLayoutChanged: () => void
  activePanelComponent: PanelComponent | null
  setActivePanelComponent: (component: PanelComponent | null) => void
  /** The specific tab id behind activePanelComponent — distinguishes which of
   *  several same-type tabs (e.g. two PDF Preview tabs) actually has focus.
   *  See synctex-pdf-viewer.tsx: only the tab whose id matches this one
   *  registers itself as the live SyncTeX/ribbon target. */
  activeTabId: string | null
  setActiveTabId: (tabId: string | null) => void
}

const LayoutContext = createContext<LayoutContextValue | null>(null)

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [closedPanels, setClosedPanels] = useState<PanelComponent[]>([])
  const [activePanelComponent, setActivePanelComponent] = useState<PanelComponent | null>(null)
  const [activeTabId, setActiveTabId] = useState<string | null>(null)
  const apiRef = useRef<LayoutApi | null>(null)

  const registerLayoutApi = useCallback((api: LayoutApi) => {
    apiRef.current = api
    setClosedPanels(api.getClosedPanels())
    return () => {
      if (apiRef.current === api) apiRef.current = null
    }
  }, [])

  const reopenPanel = useCallback((component: PanelComponent) => {
    apiRef.current?.reopenPanel(component)
    setClosedPanels(apiRef.current?.getClosedPanels() ?? [])
  }, [])

  const resetLayout = useCallback(() => {
    apiRef.current?.resetLayout()
    setClosedPanels(apiRef.current?.getClosedPanels() ?? [])
  }, [])

  const notifyLayoutChanged = useCallback(() => {
    setClosedPanels(apiRef.current?.getClosedPanels() ?? [])
  }, [])

  const value = useMemo(
    () => ({
      closedPanels,
      reopenPanel,
      resetLayout,
      registerLayoutApi,
      notifyLayoutChanged,
      activePanelComponent,
      setActivePanelComponent,
      activeTabId,
      setActiveTabId,
    }),
    [
      closedPanels,
      reopenPanel,
      resetLayout,
      registerLayoutApi,
      notifyLayoutChanged,
      activePanelComponent,
      activeTabId,
    ],
  )

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  )
}

export function useLayoutPanels() {
  const ctx = useContext(LayoutContext)
  if (!ctx) {
    throw new Error("useLayoutPanels must be used inside <LayoutProvider>")
  }
  return ctx
}
